import { useEffect, useRef, useState } from 'react';
import { useConfiguration } from '../../../../store/configurationStore';
import Actions from '../../../../store/Actions';

export type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

export const useConfigurationAutoSave = () => {
  const { templates, activeTemplateId, setSaving, setLastSavedAt } =
    useConfiguration();
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');

  // Store previous template content by template ID (not globally)
  const previousTemplateContentRef = useRef<Map<string, string>>(new Map());
  const previousActiveIdRef = useRef<string | null>(null);

  useEffect(() => {
    const activeTemplate = templates.find(t => t.id === activeTemplateId);

    if (!activeTemplate || !activeTemplateId) {
      return;
    }

    // Serialize current template
    const currentTemplateString = JSON.stringify(activeTemplate);
    const previousContent =
      previousTemplateContentRef.current.get(activeTemplateId) || '';

    // Detect navigation (template ID changed)
    const templateIdChanged =
      previousActiveIdRef.current !== null &&
      previousActiveIdRef.current !== activeTemplateId;

    if (templateIdChanged) {
      // Just navigated to this template - update tracking refs, DON'T save
      previousActiveIdRef.current = activeTemplateId;
      previousTemplateContentRef.current.set(
        activeTemplateId,
        currentTemplateString
      );
      return;
    }

    // Update previous active ID for next comparison
    previousActiveIdRef.current = activeTemplateId;

    // Check if content actually changed (same template)
    if (previousContent === '' || previousContent === currentTemplateString) {
      previousTemplateContentRef.current.set(
        activeTemplateId,
        currentTemplateString
      );
      return;
    }

    // Content changed - update ref and trigger save immediately
    previousTemplateContentRef.current.set(
      activeTemplateId,
      currentTemplateString
    );

    // Save immediately without debounce
    const saveTemplate = async () => {
      try {
        setSaving(true);
        setSaveStatus('saving');

        await Actions.saveConfigurationTemplate(activeTemplate);

        const timestamp = new Date().toISOString();
        setLastSavedAt(timestamp);
        setSaveStatus('saved');
      } catch (error) {
        console.error('Failed to save configuration template:', error);
        setSaveStatus('error');
      } finally {
        setSaving(false);
      }
    };

    saveTemplate();
  }, [templates, activeTemplateId, setSaving, setLastSavedAt]);

  return saveStatus;
};
